/**
 * The model gallery, `?gallery`: every model in world.glb on its own tile of a cleared plateau,
 * in the game's own look, to turn, zoom and judge. A tap names the model under it. For art
 * direction; players never see it.
 */
import { type Coord, Terrain } from "@mistsweeper/core";
import { type Placement, SWAY, type Sway, type WorldSource } from "@mistsweeper/renderer";

const COLUMNS = 6;
const SPACING = 2;

const FAMILIES = [
  "house",
  "cottage",
  "shed",
  "sauna",
  "barn",
  "longhouse",
  "boathouse",
  "well",
  "wellsweep",
  "rootcellar",
  "smithy",
  "beehive",
  "cart",
  "fence",
  "rengarde",
  "woodpile",
  "campfire",
  "harbre",
  "njalla",
  "fabod",
  "kolmila",
  "tjardal",
  "huntingtower",
  "beacon",
  "watermill",
  "kastal",
  "stormannagard",
  "church",
  "windmill",
  "ruin",
  "runestone",
  "stones",
  "stonecircle",
  "cairn",
  "kummel",
  "rockcarving",
  "cave",
  "gravhog",
  "shipsetting",
  "fornborg",
  "spruce",
  "pine",
  "birch",
  "snag",
  "juniper",
  "rowan",
  "oak",
  "bush",
  "fern",
  "tuft",
  "stump",
  "windthrow",
  "log",
  "mushroom",
  "rock",
  "pebbles",
  "boulder",
  "outcrop",
  "peak",
  "reeds",
  "jetty",
  "fishhut",
  "seamark",
  "flottning",
  "boat",
  "faering",
  "deer",
  "elk",
  "reindeer",
  "duck",
  "eagle",
  "banner",
];

function family(name: string): string {
  return name.replace(/^iso-/, "").replace(/-\d+$/, "");
}

function sway(name: string): Sway {
  const f = family(name);
  if (["spruce", "pine", "birch", "snag", "juniper", "rowan", "oak"].includes(f)) return SWAY.tree;
  if (["tuft", "fern", "reeds", "bush"].includes(f)) return SWAY.grass;
  return SWAY.none;
}

export interface Gallery {
  readonly world: WorldSource;
  readonly dress: (x: number, y: number) => Placement[] | null;
  nameAt(tile: Coord): string | null;
  /** Where a model stands, for tests and screenshots. */
  tileOf(name: string): Coord | null;
  /** The middle of the gallery, where the camera starts. */
  readonly centre: Coord;
}

export function makeGallery(modelNames: Iterable<string>): Gallery {
  const rank = (name: string) => {
    const i = FAMILIES.indexOf(family(name));
    return i < 0 ? FAMILIES.length : i;
  };
  const names = [...modelNames]
    .filter((n) => !/ghost|chasm|puff|-sails|-wheel|-head|-flag|-flagpole|banner-1-cairn/.test(n))
    .sort((a, b) => rank(a) - rank(b) || a.localeCompare(b, "en", { numeric: true }));
  const rows = Math.ceil(names.length / COLUMNS);
  const at = new Map<string, string>();
  names.forEach((name, i) => {
    at.set(`${(i % COLUMNS) * SPACING},${Math.floor(i / COLUMNS) * SPACING}`, name);
  });
  const inside = (x: number, y: number) =>
    x >= -1 && y >= -1 && x <= (COLUMNS - 1) * SPACING + 1 && y <= (rows - 1) * SPACING + 1;
  const world: WorldSource = {
    isRevealed: inside,
    hasBanner: () => false,
    adjacent: () => 0,
    terrain: () => Terrain.grass,
    isChasm: () => false,
    isClosed: () => false,
    isSettled: () => false,
    status: "playing",
    killer: null,
  };
  return {
    world,
    dress: (x, y) => {
      const name = at.get(`${x},${y}`);
      if (!name) return null;
      // What stands on a block of 2 by 2 tiles stands on the corner where they meet.
      const wide = /^iso-(massif|shipsetting|fornborg|stormannagard)-/.test(name) ? 0.5 : 0;
      return [{ name, dx: wide, dy: wide, yaw: 0, scale: 1, sway: sway(name) }];
    },
    nameAt: (tile) => at.get(`${tile.x},${tile.y}`) ?? null,
    tileOf: (name) => {
      const i = names.indexOf(name);
      return i < 0 ? null : { x: (i % COLUMNS) * SPACING, y: Math.floor(i / COLUMNS) * SPACING };
    },
    centre: {
      x: Math.round(((COLUMNS - 1) * SPACING) / 2),
      y: Math.round(((rows - 1) * SPACING) / 4),
    },
  };
}
